/*
 * ------------------------------------------------------------
 * TEMPORARY: cooker launch PDP. Remove after 30 Nov 2026 (see snippets/pc-cooker-head.liquid).
 * Behaviour for sections/pc-section-nav.liquid.
 *
 * The links are plain #anchors; this adds:
 * - Smooth scroll that lands the section just under the sticky tab bar (and the header when it will be shown:
 *   assets/pc-cooker.js hides the header on scroll down and shows it on scroll up).
 * - The active tab (aria-current) for the section being read.
 * - Closing the phone menu (theme <openable-element>) after a pick; tabs for sections not on the page are hidden.
 *
 * theme.js <scroll-spy> was checked: it is the vertical FAQ sidebar (SVG path, marks the next section rather
 * than the current one, and fails when a link has no target), so it doesn't fit a horizontal tab bar.
 * Reuses getStickyHeaderOffset() from theme.js.
 * ------------------------------------------------------------
 */
(() => {
  if (window.customElements.get("pc-section-nav")) {
    return;
  }

  const ACTIVE_TOLERANCE = 40; // px below the bar a section's top may be and still count as the one being read
  const LAND_GAP = 12; // px between the bar and a section after jumping to it

  class PcSectionNav extends HTMLElement {
    connectedCallback() {
      this.bar = this.querySelector(".pc-nav__bar");
      this.menu = this.querySelector(".pc-nav__menu");
      this.links = Array.from(this.querySelectorAll("[data-pc-nav-link]"));

      /* Hide tabs whose section isn't on the page */
      this.links.forEach((link) => {
        if (!this._target(link)) {
          link.parentElement.hidden = true;
        }
      });

      /* One entry per section, in page order (each section has a bar link and a menu link) */
      const seen = new Set();
      this.targets = this.links
        .map((link) => this._target(link))
        .filter((target) => target && !seen.has(target) && seen.add(target))
        .sort((a, b) => (a.compareDocumentPosition(b) & Node.DOCUMENT_POSITION_FOLLOWING ? -1 : 1));

      this.active = null;
      this.raf = 0;

      /* Capture: the menu's openable-element stops some clicks from bubbling */
      this.addEventListener("click", this._onClick.bind(this), true);

      this._onScroll = () => {
        if (!this.raf) {
          this.raf = requestAnimationFrame(() => {
            this.raf = 0;
            this._markActive();
          });
        }
      };

      window.addEventListener("scroll", this._onScroll, { passive: true });
      window.addEventListener("resize", this._onScroll);
      this._markActive();
    }

    disconnectedCallback() {
      window.removeEventListener("scroll", this._onScroll);
      window.removeEventListener("resize", this._onScroll);
      cancelAnimationFrame(this.raf);
    }

    _target(link) {
      return document.getElementById(link.getAttribute("href").slice(1));
    }

    /* Height of the tab bar when it shows (desktop); 0 on phones where it is hidden */
    get barHeight() {
      return this.bar && this.bar.offsetParent !== null ? this.bar.offsetHeight : 0;
    }

    _onClick(event) {
      const link = event.target.closest("[data-pc-nav-link]");
      const target = link && this._target(link);

      if (!target) {
        return;
      }

      event.preventDefault();

      if (this.menu) {
        this.menu.open = false;
      }

      /* Scrolling down hides the header, scrolling up brings it back, so only leave room for it going up */
      const targetTop = target.getBoundingClientRect().top + window.scrollY;
      const goingUp = targetTop < window.scrollY;
      const headerHeight = parseInt(getComputedStyle(document.documentElement).getPropertyValue("--header-height")) || 0;
      const offset = this.barHeight + (goingUp ? headerHeight : 0) + LAND_GAP;

      window.scrollTo({ top: targetTop - offset, behavior: "smooth" });
    }

    /* Active = the last section whose top has reached the bar */
    _markActive() {
      const line = getStickyHeaderOffset() + this.barHeight + ACTIVE_TOLERANCE;
      let active = null;

      this.targets.forEach((target) => {
        if (target.getBoundingClientRect().top <= line) {
          active = target;
        }
      });

      if (active === this.active) {
        return;
      }

      this.active = active;
      const activeHref = active ? `#${active.id}` : null;

      this.links.forEach((link) => {
        if (link.getAttribute("href") === activeHref) {
          link.setAttribute("aria-current", "true");
        } else {
          link.removeAttribute("aria-current");
        }
      });
    }
  }

  window.customElements.define("pc-section-nav", PcSectionNav);
})();
